import {
  MediaPickerComponent,
  MediaType,
  NewMessage,
  PickerMode,
} from '@gripp/shared-logic';
import { IssueFooter } from '../issue/issueFooter';
import { StyleProp, ViewStyle } from 'react-native';

export type MessageThreadComposerProps = {
  /** Called when the user submits a new message. */
  onPostMessage: (message: NewMessage) => Promise<void>;
  /**
   * The platform media picker component to render inside the composer.
   * Passed through to IssueFooter unchanged — the host supplies the correct
   * picker for web vs. native.
   */
  mediaPicker: MediaPickerComponent;
  /** Accepted media types for the attachment picker. Defaults to Image + Document + Video. */
  acceptedMediaTypes?: MediaType[];
  /** Workspace ID forwarded to the media picker for upload scoping. */
  workspaceId?: string;
  /**
   * The activity being composed in, if any. Forwarded to IssueFooter's
   * mention field so a guest with a live grant on this activity is
   * mentionable inside it; omit for a not-yet-created activity.
   */
  issueId?: string;
  /** Picker mode. Defaults to ATTACHMENT on web. */
  mode?: PickerMode;
  /** Optional style override for the composer container. */
  containerStyle?: StyleProp<ViewStyle>;
  /** Placeholder text shown in the compose input. */
  placeholder?: string;
};

/**
 * MessageThreadComposer
 *
 * Extracted compose-input + attach-button slice from issue.tsx / IssueFooter.
 * This is a thin wrapper around IssueFooter — the existing battle-tested
 * component already handles media preview, send/clear, keyboard height
 * management, and web vs. native media picker differences. We forward all
 * props through so callers can configure it identically to IssueFooter.
 *
 * If IssueFooter gains new props that are needed here, extend this type first
 * rather than bypassing MessageThreadComposer.
 */
export const MessageThreadComposer = (props: MessageThreadComposerProps) => {
  return (
    <IssueFooter
      onNewMessage={props.onPostMessage}
      mediaPicker={props.mediaPicker}
      acceptedMediaTypes={
        props.acceptedMediaTypes ?? [
          MediaType.Image,
          MediaType.Document,
          MediaType.Video,
        ]
      }
      workspaceId={props.workspaceId}
      issueId={props.issueId}
      mode={props.mode ?? PickerMode.ATTACHMENT}
      containerStyle={props.containerStyle}
      placeholder={props.placeholder}
    />
  );
};
